import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Server,
  AlertTriangle,
  Wrench,
  FileText,
  Activity,
  Moon,
  Sun,
  Monitor,
  LogOut,
  Users,
  Rocket,
  GitBranch,
} from 'lucide-react';
import { useThemeStore } from '@/shared/hooks/useTheme.ts';
import { useAuthStore } from '@/shared/hooks/useAuthStore.ts';
import { usePendingDeployDraftsCount } from '@/shared/hooks/useDeployDraftsCount.ts';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/tenants', label: 'Tenants', icon: Server, end: false },
  { to: '/admin/incidents', label: 'Incidentes', icon: AlertTriangle, end: false },
  { to: '/admin/maintenances', label: 'Manutenções', icon: Wrench, end: false },
  // `badge: 'deployDrafts'` liga este item ao contador de deploys pendentes.
  { to: '/admin/deploys', label: 'Deploys', icon: Rocket, end: false, badge: 'deployDrafts' },
  { to: '/admin/deploy-sources', label: 'Repositórios', icon: GitBranch, end: false },
  { to: '/admin/probe-logs', label: 'Probe Logs', icon: Activity, end: false },
  { to: '/admin/reports', label: 'Relatórios', icon: FileText, end: false },
  { to: '/admin/users', label: 'Usuários', icon: Users, end: false },
];

export function AdminLayout() {
  const { theme, toggle } = useThemeStore();
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { data: deployDraftsPendentes } = usePendingDeployDraftsCount();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="flex h-screen bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Sidebar */}
      <aside className="flex w-56 flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="flex h-14 items-center gap-2 border-b border-gray-200 px-4 dark:border-gray-800">
          <img src="/logo-woli-positivo.png" alt="Woli" className="w-20 object-contain dark:hidden" />
          <img src="/logo-woli-negativo.png" alt="Woli" className="hidden w-20 object-contain dark:block" />
          <span className="text-xs text-gray-400">Uptime Monitor</span>
        </div>

        <nav className="flex-1 space-y-1 px-2 py-3">
          {navItems.map(({ to, label, icon: Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                }`
              }
            >
              <Icon size={18} />
              {label}
              {badge === 'deployDrafts' && !!deployDraftsPendentes && (
                <span className="ml-auto inline-flex min-w-[1.25rem] justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                  {deployDraftsPendentes}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className="border-t border-gray-200 px-2 py-3 dark:border-gray-800">
          <div className="flex items-center gap-1 px-1 mb-2">
            <button
              onClick={() => { if (theme !== 'light') toggle(); }}
              className={`flex items-center justify-center rounded-md p-2 transition-colors ${
                theme === 'light'
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title="Modo claro"
            >
              <Sun size={16} />
            </button>
            <button
              onClick={() => { if (theme !== 'dark') toggle(); }}
              className={`flex items-center justify-center rounded-md p-2 transition-colors ${
                theme === 'dark'
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title="Modo escuro"
            >
              <Moon size={16} />
            </button>
            <button
              onClick={() => {}}
              className="flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Modo sistema"
            >
              <Monitor size={16} />
            </button>
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-6 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Painel Administrativo
          </h2>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
